/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import {useAuthenticatedUser} from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  BulkActionEntity,
  BulkActionType,
  CarbDevices,
  GenericObject,
  OperationTask,
  SentinelDevices
} from '@ion/core/src/types/entities';
import {
  APIGatewayMemberSearch,
  APIGatewaySmartSearch,
  HierLevel,
  SmartSearchCriteria
} from '@ion/core/src/types/services';
import {getCustomWorkBook} from '@ion/core/src/xlsx/ExcelHelpers';
import {IonDialog} from '@ion/dls/src/components/dialogs/IonDialog';
import {IonStepperActionButtons} from '@ion/dls/src/components/dialogs/IonStepperActionButtons';
import {useFormState} from '@ion/dls/src/hooks/useFormState';
import {BulkActionProps} from '@ion/dls/src/imports/BulkUploadContainer';
import {formatErrorMessage, serviceEnablementLabels} from '@ion/dls/src/utils/ComponentUtils';
import {useBulkOperation} from '@ion/query/src/hooks/useBulkOperation';
import {useS3BucketUrl} from '@ion/query/src/hooks/useS3BucketUrl';
import useEntityLabel from '../hooks/useEntityLabel';
import {useFilePermissionsAction} from '../hooks/useFilePermissionsAction';
import {useGlobalAccountSolomonId} from '../hooks/useGlobalAccountSolomonId';
import {getAllSCIDevices, getDeviceIdsList} from '../pages/devices/SCISettingsUtils';
import {useServiceBasedFeatures} from '../pages/devices/useServiceBasedFeatures';
import {useVbusAccess} from '../pages/devices/useVbusAccess';
import {useVbusEnabledAccess} from '../pages/devices/useVbusEnabledAccess';
import {DEFAULT_REGION} from '../pages/manufacturing/inventory/helpers';
import {useCreateAPISmartSearchCriteria} from './hooks/useCreateAPISmartSearchCriteria';
import {isConflictsChecked} from './store/store';
import {ActionSelectionStep} from './ActionSelectionStep';
import {
  BulkSelectEntitiesFormFields,
  excludeRemovedDevicesFromSearchCriteria,
  getCarbDevices,
  getInitialBulkSelectEntitiesFormState,
  getPairedDevices,
  isValidFile,
  LMU_DEVICE_TYPE,
  MAXIMUM_ENTITY_COUNT,
  shouldUseNoConflictsDevices
} from './BulkSelectEntitiesEditorUtils';
import EntitySelectionStep from './EntitySelectionStep';
import {useActionForm} from './useActionForm';
import {useBulkActionDialog} from './useBulkActionDialog';
import {BulkEntitiesImportParamsType} from './useBulkSelectedEntitiesImport';
import useEntityActions from './useEntityActions';
import {useScheduleDeviceSyncMaxExceeded} from './useScheduleDeviceSyncMaxExceeded';
import {useTaskNickName} from './useTaskNickName';

export interface BulkActionDialogProps extends BulkActionProps {
  onComplete?: (message: string) => void;
  accountId?: number;
  userId?: number;
  hasCreateJob?: boolean;
  hasUpdateAsset?: boolean;
  hasCreateDevice?: boolean;
  Features?: GenericObject;
  omitExtensions?: boolean;
  hasVehicleProfile?: boolean;
  hasDevicePassword?: boolean;
  isSupportedRole?: boolean;
  hasTeleCryptEnabled?: boolean;
  jobSearchEngineEnabled?: boolean;
  serviceEnablements: GenericObject;
  featureFlagProps: GenericObject;
};

export enum BulkActionSteps {
  ENTITY,
  ACTION
};
const carbColumns = [
  'identifiersList',
  'VIN(Optional)',
  'License Plate(Optional)',
  'License State(Optional)',
  'Registration Renewal Date(Optional)'
];

enum ActionType {
  CANCEL_DEVICES_FROM_JOBS = 'cancelDevicesFromJobs'
};

/**
 * Feature dialog that displays a stepper inside of a dialog for users to bulk select entities
 * & perform actions on them.
 *
 * ```typescript
 *
 *  import {BulkActionDialog} from '@ion/applications/src/bulk';
 *
 *  <BulkActionDialog />
 *
 * ```
 */
export const BulkActionDialog = ({
  userId,
  Features,
  accountId,
  onComplete,
  serviceEnablements,
  featureFlagProps,
  isBulkAction = false,
  hasCreateJob = false,
  hasUpdateAsset = false,
  hasCreateDevice = false,
  omitExtensions = false,
  hasVehicleProfile = false,
  hasDevicePassword = false,
  isSupportedRole = false,
  hasTeleCryptEnabled = false,
  jobSearchEngineEnabled = false
}: BulkActionDialogProps) => {
  const {
    close,
    selectedGridData : {
      selectedChips: selectedRowEntities,
      excludedDevices,
      totalRowsCount,
      areTotalRowsSelected,
      entity: entityType,
      overrideBulkEntityType,
      search,
      onRefresh,
      filters,
      defaultIdentifier = 'id',
      isProvisioning = false,
      customSearch = false,
      showFileOption = true
    }
  } = useBulkActionDialog();

  const isSelectedGridList                          = !!(selectedRowEntities?.length || areTotalRowsSelected);
  const entitySelectionRef                          = useRef();
  const {ACTION, ENTITY}                            = BulkActionSteps;
  const firstStepToShow                             = isSelectedGridList ? ACTION : ENTITY;
  const [activeStep, setActiveStep]                 = useState<BulkActionSteps>(firstStepToShow);
  const [isFirstStep, setIsFirstStep]               = useState(activeStep === ENTITY);
  const [pastedIds, setPastedIds]                   = useState<string[]>([]);
  const [bulkImportParams, setBulkImportParams]     = useState<BulkEntitiesImportParamsType>(null);
  const actions                                     = useEntityActions(entityType);
  const [actionType, setActionType]                 = useState<BulkActionType>(actions[0]?.type);
  const [entityIdentifiers, setEntityIdentifiers]   = useState([]);
  const [hasFile, setHasFile]                       = useState(false);
  const [selectedSCIDevices, setSelectedSCIDevices] = useState([]);
  const [selectedSCITypes, setSelectedSCITypes]     = useState<string[]>([]);
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>('');
  const [formSumbit, setFormSumbit]                 = useState<GenericObject>();
  const [customFileUpload, setCustomFileUpload]     = useState(false);
  const [entitiesCount, setEntitiesCount]           = useState<number>();
  const [searchCriteria, setSearchCriteria]         = useState<APIGatewaySmartSearch | APIGatewayMemberSearch>();
  const [hierLevel, setHierlevel]                   = useState(HierLevel.ALL);
  const [identifiers, setIdentifiers]               = useState([]);
  const [noConflictsDevices, setNoConflictsDevices] = useState([]);
  const [isCancelLoading, setIsCancelLoading]       = useState(false);
  const [pairedDevices, setPairedDevices]           = useState<SentinelDevices>([]);
  const [sentinelDevices, setSentinelDevices]       = useState<SentinelDevices>([]);
  const [nonSentinelDevices, setNonSentinelDevices] = useState([]);
  const [carbDevices, setCarbDevices]               = useState<CarbDevices>([]);
  const [carbFinalDevices, setCarbFinalDevices]     = useState<CarbDevices>([]);
  const [description, setDescription]               = useState(null);
  const [isSentinel, setIsSentinel]                 = useState(false);

  const [showSentinelWarning, setShowSentinelWarning] = useState(false);

  const [removedConflictDeviceIds, setRemovedConflictDeviceIds] = useState<number[]>([]);
  const [
    isProcessButtonDisabled,
    setIsProcessButtonDisabled
  ] = useState(false);

  const smartSearchCriteria = useCreateAPISmartSearchCriteria(search as SmartSearchCriteria, customSearch);
  const serviceOptions      = useServiceBasedFeatures({
    serviceEnablements,
    Features,
    serviceEnablementLabels
  });
  const hasVBusAccess       = useVbusEnabledAccess(Features);
  const isCalampSupportRole = useVbusAccess(true);
  const hasFilePermissions  = useFilePermissionsAction({Features});
  const solomonId           = useGlobalAccountSolomonId();
  const {account}           = useAuthenticatedUser();
  const loggedInAccountId   = entityId(account);

  const isMaxExceeded = useScheduleDeviceSyncMaxExceeded({
    allRowsSelected : areTotalRowsSelected,
    rowCount        : totalRowsCount,
    selectedCount   : selectedRowEntities?.length,
    pastedIds       : pastedIds?.length
  });

  useEffect(() => {
    if (!customSearch) {
      if (smartSearchCriteria) {
        const smartSearchModifiedForBulkOperation = {...smartSearchCriteria};
        setHierlevel(smartSearchModifiedForBulkOperation.smartSearch.search.hierLevel);

        /**
         * since the bulk operation api relies on the accountId & hierLevel of the request versus search,
         * we remove those fields in the search since they aren't used.
         */
        delete smartSearchModifiedForBulkOperation.smartSearch.search.accountId;
        delete smartSearchModifiedForBulkOperation.smartSearch.search.hierLevel;
        setSearchCriteria(smartSearchModifiedForBulkOperation);
      } else {
        const memberSearch = search?.searchCriteria[0]?.memberSearch;
        if (memberSearch) {
          setHierlevel(memberSearch?.hierLevel);
          let searchTerms = {...memberSearch.searchTerms};
          delete searchTerms?.account;
          if (areTotalRowsSelected && isEmpty(searchTerms)) {
            searchTerms = {name: '***'};
          }
          if (searchTerms?.groups) {
            searchTerms = {
              ...searchTerms,
              groups : searchTerms?.groups === -1 ? null : searchTerms?.groups
            };
          }
          const search = {
            memberSearch : {
              search : {
                searchTerms : {
                  ...searchTerms
                }
              }
            }
          };
          setSearchCriteria(search);
        }
      }
    }
    if (customSearch && isEqual(entityType, BulkActionEntity.DEVICES)) {
      if (search?.sortOn) {
        const sortItems = search?.sortOn?.sort || [];
        const sort = [];
        sortItems?.forEach(item => {
          sort.push(`${Object.values(item)[0].order === 'desc' ? '-' : '' }${Object.keys(item)[0]}`);
        });
        setHierlevel(search?.sortOn?.hierLevel);
        setSearchCriteria({
          smartSearch : {
            search : {...search?.sortOn, sort}
          }
        });
      } else {
        const anySearch = search as APIGatewaySmartSearch;
        setHierlevel(anySearch?.smartSearch?.search?.hierLevel);
        setSearchCriteria(anySearch);
      }
    }
  }, [areTotalRowsSelected, search, smartSearchCriteria]);

  const [
    entitySelectionFormState,
    setEntitySelectionFormState
  ] = useFormState(getInitialBulkSelectEntitiesFormState());

  const {
    singularEntityLabel,
    pluralEntityLabel
  } = useEntityLabel(entityType);

  const {
    shared,
    bulkActions,
    choosers
  } = translate('common:editors', {
    returnObjects : true,
    entity        : singularEntityLabel,
    entities      : pluralEntityLabel,
    pastedCount   : pastedIds?.length
  });

  const identifierTypes = choosers.identifierTypes[entityType]?.options;

  // the first option for an entity identifier type is the default for file & row selection based operations
  const [selectedIdentifierType, setSelectedIdentifierType] = useState<string>(identifierTypes[0]?.value);

  const {process, isProcessing}       = useBulkOperation();
  const {uploadFile, isUploadingFile} = useS3BucketUrl();

  const primaryLabel = useMemo(() => {
    if (actionType === BulkActionType.JOB_CREATION) {
      return isConflictsChecked.value
        ? translate('admin-devices:createJob.dialog.submit')
        : translate('common:next');
    }
    return isFirstStep
      ? shared[translate('common:next')]
      : shared[translate('common:process')];
  }, [actionType, isFirstStep, shared, isConflictsChecked.value]);

  useEffect(function(): void {
    if (selectedRowEntities?.length) {
      setEntitiesCount(selectedRowEntities?.length);
      const {pairedDevices, unallowedDevices} = getPairedDevices(selectedRowEntities, 'esn', true);
      setNonSentinelDevices(unallowedDevices);
      setPairedDevices(pairedDevices);
      setShowSentinelWarning(true);
      setCarbDevices(getCarbDevices({devices: selectedRowEntities, defaultIdentifier: 'id'}));
      setIdentifiers(selectedRowEntities.map(rowEntity => (rowEntity[defaultIdentifier])));
      setEntityIdentifiers(selectedRowEntities.map(rowEntity => (rowEntity[defaultIdentifier]))
      );
    }
    if (pastedIds?.length) {
      setEntitiesCount(pastedIds?.length);
      setIdentifiers(pastedIds);
      setPairedDevices(getPairedDevices(pastedIds)?.pairedDevices);
      setCarbDevices(getCarbDevices({devices: pastedIds}));
      setCustomFileUpload(pastedIds?.length > MAXIMUM_ENTITY_COUNT);
      setEntityIdentifiers([{name: bulkActions.dialogBulkAction.actionSelection.selectionChips.pasted}]);
    }
    if (isValidFile(bulkImportParams)) {
      setHasFile(true);
      setPairedDevices(getPairedDevices(bulkImportParams?.items, defaultIdentifier)?.pairedDevices);
      setCarbDevices(getCarbDevices({devices: bulkImportParams?.items, defaultIdentifier}));
      setIdentifiers(bulkImportParams?.items?.map(item => item?.deviceIdentifier));
      setEntitiesCount(bulkImportParams?.items?.length);
      setEntityIdentifiers([{name: bulkImportParams?.fileName}]);
    }
  }, [pastedIds, selectedRowEntities, bulkImportParams]);

  const previouslyImportedFile = entitySelectionFormState[BulkSelectEntitiesFormFields.File]?.value?.importParams;

  useEffect(function(): void {
    setHasFile(!!isValidFile(previouslyImportedFile));
  }, [previouslyImportedFile]);

  useEffect(() => {
    setIsProcessButtonDisabled(isSentinel && !pairedDevices?.length);
  }, [isSentinel, pairedDevices]);

  useEffect(function(): void {
    if (selectedRowEntities?.length) {
      const selectedDevices = getAllSCIDevices(selectedRowEntities);
      const getSCIDevcies   = selectedDevices?.sciDevices;
      setSelectedSCIDevices([...getSCIDevcies]);
      setSelectedSCITypes([...selectedDevices?.selectedAllTypes]);
      setSelectedDeviceType((getSCIDevcies?.length && getSCIDevcies[0].length) ? getSCIDevcies[0][0]?.typeName : '');
    }
  }, [selectedRowEntities]);

  const getEntityIdentifiers = () => {
    if (actionType === BulkActionType.SERVICE_ENABLEMENT &&
      isSentinel && (entityIdentifiers?.length !== pairedDevices?.length)) {
      return pairedDevices;
    }
    return entityIdentifiers;
  };

  const {
    taskNickName,
    setTaskNickName,
    setTaskNickNameAction,
    setTaskNickNameUserModified
  } = useTaskNickName({
    pastedIdCount     : pastedIds?.length,
    entityType,
    actions,
    actionType,
    identifierTypes,
    selectedIdentifierType,
    hasFile,
    bulkImportParams,
    selectedRowEntities,
    entityIdentifiers : getEntityIdentifiers(),
    areTotalRowsSelected,
    totalRowsCount    : areTotalRowsSelected && excludedDevices?.length
      ? (totalRowsCount-excludedDevices?.length) : totalRowsCount
  });

  const getSCIDevices = () => getDeviceIdsList(selectedSCIDevices[selectedSCITypes.indexOf(selectedDeviceType)]);

  const isJobCreation = isEqual(actionType, BulkActionType.JOB_CREATION);

  const getIdentifierList = (isCarb): string[] | CarbDevices => {
    if (isCarb) {
      return carbFinalDevices;
    }

    if (shouldUseNoConflictsDevices({
      areTotalRowsSelected,
      isJobCreation,
      jobSearchEngineEnabled,
      noConflictsDevices,
      identifierType : selectedIdentifierType
    })) {
      return noConflictsDevices;
    }
    if (pastedIds?.length) {
      return pastedIds;
    }
    if (!areTotalRowsSelected && selectedRowEntities?.length) {
      if (isEqual(actionType, BulkActionType.SCI_UPDATE)) {
        return getSCIDevices();
      } else {
        return entityIdentifiers;
      }
    }
    return [];
  };

  const getActionType = (): BulkActionType | ActionType => {
    if (actionType === BulkActionType.BULK_EDIT) {
      return BulkActionType.COMMON_FIELD_UPDATE;
    } else if (actionType === BulkActionType.VBUS) {
      return BulkActionType.JOB_CREATION;
    } else if (actionType === BulkActionType.BULK_ADD) {
      return BulkActionType.COMMON_FIELD_CREATE;
    } else if (actionType === BulkActionType.CANCEL_FROM_ALL_JOBS) {
      return ActionType.CANCEL_DEVICES_FROM_JOBS;
    } else if (actionType === BulkActionType.SERVICE_ENABLEMENT && sentinelDevices?.length) {
      return BulkActionType.SENTINEL_SYNC;
    } else if (actionType === BulkActionType.SERVICE_ENABLEMENT && carbFinalDevices?.length) {
      return BulkActionType.CARB;
    } else if (actionType === BulkActionType.SCHEDULE_DATA_SYNC) {
      return BulkActionType.SCHEDULE_DEVICE_SYNC;
    }
    return actionType;
  };

  const onProcessValidationSuccess = (formDetails: GenericObject, updatedTaskNickName: string) => {
    const testProfileAction = isEqual(actionType, BulkActionType.VEHICLE_PROFILE);
    const devicePasswordAction = isEqual(actionType, BulkActionType.DEVICE_PASSWORD);
    const assignRegionAction = isEqual(actionType, BulkActionType.ASSIGN_REGION);
    const onBulkOperation = (s3DownloadKey?: string, isEsnId: boolean = false, isCarb = false) => {
      process?.({
        entityIdentifierS3Path : s3DownloadKey,
        entityIdentifierList   : getIdentifierList(getActionType() === BulkActionType.CARB),
        entityIdentifierType   : isEsnId ? 'esn' : selectedIdentifierType,
        taskNickName           : updatedTaskNickName,
        bulkOperation          : {
          actionType : getActionType(),
          ...(!devicePasswordAction &&
                (testProfileAction
                  ? {action: formDetails?.testProfile}
                  : {actionDetails: assignRegionAction ? {provisionAccountId: accountId} : formDetails}
                )),
          ...(devicePasswordAction && {emailRecipients: formDetails})
        },
        currRegion     : DEFAULT_REGION,
        search         : search?.search ?? search,
        entityType     : overrideBulkEntityType || entityType,
        accountId      : assignRegionAction ? loggedInAccountId : accountId,
        isProvisioning,
        solomonId,
        hierLevel,
        searchCriteria : isJobCreation && areTotalRowsSelected
          ? excludeRemovedDevicesFromSearchCriteria(searchCriteria, removedConflictDeviceIds)
          : searchCriteria
      }, {
        onSuccess : (_task: OperationTask) => {
          onComplete(bulkActions.snackbar.success);
          close();
          onRefresh?.();
        },
        onError : (err) => {
          onComplete(formatErrorMessage(err) || bulkActions.snackbar.error);
          error('Unable to process the provided bulk operation due to: ', err);
        }
      });
    };
    const isSentinelSync = getActionType() === BulkActionType.SENTINEL_SYNC;
    const isCarb = getActionType() === BulkActionType.CARB && (hasFile || carbFinalDevices?.length > 20);
    if (hasFile || customFileUpload || isSentinelSync || isCarb) {
      const customWorkBook = (customFileUpload || isSentinelSync || isCarb) && getCustomWorkBook(
        isSentinelSync
          ? sentinelDevices
          : isCarb
            ? carbFinalDevices
            : pastedIds,
        String(entityType),
        isSentinelSync || isCarb,
        isSentinelSync ? 'sentinel' : 'carb',
        isCarb ? carbColumns : undefined);
      uploadFile({
        actionType : getActionType(),
        workbook   : (customFileUpload || isSentinelSync || isCarb) ? customWorkBook : bulkImportParams?.workbook,
        entityType : String(entityType),
        userId,
        accountId
      }, {
        onSuccess : (s3DownloadKey) => {
          const isEsnId = isSentinelSync && !!selectedRowEntities?.length && !areTotalRowsSelected;
          onBulkOperation(s3DownloadKey, isEsnId, isCarb);
        },
        onError : (err) => {
          error('Unable to upload the provided file due to error: ', err);
        }
      });
    } else {
      onBulkOperation();
    }
  };

  const processFormSubmission = useCallback((formDetails: GenericObject) => {
    setFormSumbit(formDetails);
  }, []);

  useEffect(() => {
    if (formSumbit && taskNickName) {
      setFormSumbit(undefined);
      onProcessValidationSuccess(formSumbit, taskNickName);
    }
  }, [formSumbit, taskNickName]);

  const onBack = useCallback(function() {
    onEntityStep();
  }, []);

  const {
    form,
    onProcess: validateActionForm
  } = useActionForm({
    jobSearchEngineEnabled,
    noConflictsDevices,
    setNoConflictsDevices,
    setRemovedConflictDeviceIds,
    setIsCancelLoading,
    accountId,
    entity                : entityType,
    onSave                : processFormSubmission,
    selectedEntitiesCount : entitiesCount,
    selectedIdentifiers   : identifiers,
    hasFile,
    customFileUpload,
    userId,
    isSelectedFromGrid    : !!selectedRowEntities?.length && !areTotalRowsSelected,
    isAllDevicesSelected  : areTotalRowsSelected && !filters?.length,
    isAllDevSelected      : Boolean(areTotalRowsSelected && filters?.length && totalRowsCount < 1000),
    isIdentifiersPasted   : !!pastedIds?.length,
    pairedDevices,
    setPairedDevices,
    setSentinelDevices,
    setNonSentinelDevices,
    carbDevices,
    setCarbFinalDevices,
    setIsSentinel,
    nonSentinelDevices,
    showSentinelWarning,
    onBack,
    serviceOptions,
    areTotalRowsSelected,
    actionType,
    selectedRowEntities,
    isSelectedGridList,
    taskNickName,
    setTaskNickName,
    setTaskNickNameAction,
    description,
    setSelectedSCIDevices,
    selectedSCIDevices,
    setSelectedSCITypes,
    selectedSCITypes,
    setSelectedIdentifierType,
    setSelectedDeviceType,
    selectedDeviceType,
    selectedIdentifierType,
    entitySelectionFormState,
    bulkImportParams,
    omitExtensions,
    Features,
    setIsProcessButtonDisabled
  });

  useEffect(() => {
    setIsFirstStep(activeStep === ENTITY);
  }, [activeStep]);

  useEffect(() => {
    setIsProcessButtonDisabled(false);
    setIsSentinel(false);
  }, [actionType]);

  const closeDialog = useCallback((e) => {
    setBulkImportParams(null);
    close(e);
  }, []);

  const onEntityStep = () => {
    setActiveStep(ENTITY);
  };

  const onValidationSuccess = useCallback((serializedDetails): void => {
    setBulkImportParams(serializedDetails[BulkSelectEntitiesFormFields.File].importParams);
    setPastedIds(serializedDetails[BulkSelectEntitiesFormFields.EntityIdentifiers]);
    setSelectedIdentifierType(serializedDetails[BulkSelectEntitiesFormFields.EntityIdentifierType].value);
    setActiveStep(ACTION);
  }, []);

  const onActionStep = (e: MouseEvent) => {
    entitySelectionRef?.current?.onSubmit?.(entitySelectionFormState, e);
  };

  const onPrimary = function(e: MouseEvent) {
    if (isFirstStep) {
      onActionStep(e);
    } else {
      if (taskNickName.length) {
        validateActionForm?.(e);
      }
    }
  };

  const createJobLabel = isConflictsChecked.value
    ? translate('admin-devices:createJob.dialog.submit')
    : translate('common:next');

  /**
   * Given a step, returns the corresponding step component to display in the dialog.
   * Maps the steps via enum (index) to their respective components.
   */
  const getComponent = (): ReactNode => {
    switch (activeStep) {
      case ACTION:
        return <ActionSelectionStep actions={actions}
          entity={entityType}
          hasCreateJob={hasCreateJob}
          hasUpdateAsset={hasUpdateAsset}
          hasCreateDevice={hasCreateDevice}
          areTotalRowsSelected={areTotalRowsSelected}
          entityIdentifiers={entityIdentifiers}
          selectedRowEntities={selectedRowEntities}
          form={form}
          disableScheduleDeviceSync={isCalampSupportRole ? isMaxExceeded : true}
          disableServiceEnablement={!serviceOptions?.length}
          selectedIdentifierType={selectedIdentifierType}
          onActionChange={setActionType}
          actionType={actionType}
          taskNickName={taskNickName}
          setTaskNickName={setTaskNickName}
          description={description}
          setDescription={setDescription}
          hasFilePermissions={hasFilePermissions}
          totalRowsCount={totalRowsCount}
          hasVehicleProfile={hasVehicleProfile}
          hasDevicePassword={hasDevicePassword}
          hasVBusAccess={hasVBusAccess}
          setTaskNickNameUserModified={setTaskNickNameUserModified} />;
      default:
        return <EntitySelectionStep ref={entitySelectionRef}
          showFileOption={showFileOption}
          entity={entityType}
          formState={entitySelectionFormState}
          setFormState={setEntitySelectionFormState}
          hasFile={hasFile}
          onValidationSuccess={onValidationSuccess}
          identifierTypes={identifierTypes}
          isBulkAction={isBulkAction} />;
    }
  };

  return (
    <IonDialog onClose={closeDialog}
      isMaxSize
      open
      title={bulkActions.title}
      actions={(
        <IonStepperActionButtons disableSecondary={isFirstStep || isSelectedGridList}
          primaryLabel={primaryLabel}
          isPrimaryLoading={isProcessing || isUploadingFile || isCancelLoading}
          onPrimary={onPrimary}
          onSecondary={onBack}
          disabled={isProcessButtonDisabled} />
      )}>
      {getComponent()}
    </IonDialog>
  );
};

export default BulkActionDialog;
